import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Typography, Row, Col, Button, Divider, Collapse, Input, Tabs,
    Select, Spin, Empty, List, Switch
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useIconSelection } from './iconContext.js';
import ModuleThumbnail from './ModuleThumbnail.js';
import MapSettings from './mapSettings.js';
import MapActiveQuests from './MapActiveQuests.js';
import { scaleSvgIcon } from './mapIconUtils.js';
import { getMapIcons } from '../../data/icons/mapIcons.js';
import getImageId from './imageIds.js';
import { GAME_MODES } from './mapMain.js';
import HelpPopover from './HelpPopover';

const { Text } = Typography;
const { Option } = Select;
const { Search: AntSearch } = Input;


function cleanMonsterId(monsterId) {
    if (!monsterId || typeof monsterId !== 'string') return 'Unknown';
    return monsterId.replace(/^(Id_Monster_|Id_Props_|Id_Spawner_New_LootDrop_|Id_Spawner_LootDrop_|Id_Spawner_New_Lootdrop_Spawn_|Id_Spawner_New_Props_|ID_LootDropGroup_)/, '').replace(/_/g, ' ');
}

function cleanModuleNameForDisplay(moduleName) {
    if (!moduleName || typeof moduleName !== 'string') return 'Unnamed Module';
     return moduleName.replace(/_/g, ' ').replace(/([a-z])([A-Z0-9])/g, '$1 $2');
 }


const NAME_REMAPPING = {
    "BP Statue01 0": "Shrine of Health",
    "BP Statue02 1": "Shrine of Protection",
    "BP Statue01 C 1": "Shrine of Health",
    "BP Statue04 0": "Fountain of Speed",
    "BP Statue02 0": "Shrine of Power",
    "Weapon Common": "Weapon",
    "Armor GoldenRoom": "Armor",
    "Armor Armory": "Armor",
    "Id LootDropGroup ChocolateChipMuffin": "Chocolate Chip Muffin",
    "Accessory OldRustRoom": "Accessory",
    "SkeletonWoodenBarrel": "Skeleton Wooden Barrel",
    "BP FixedStairDown C 0": "Stairs Down",
    "BP FixedStairDown C 1": "Stairs Down",
    "BP FixedStairDown C 2": "Stairs Down",
    "BP FixedStairDown C 3": "Stairs Down",
    "BP FixedStairDown C 4": "Stairs Down",
    "BP FixedStairDown C 5": "Stairs Down",
    "BP FixedStairDown C 6": "Stairs Down",
    "BP FixedStairDown C 7": "Stairs Down",
    "BP FixedStairDown C 8": "Stairs Down",
    "BP FixedStairDown C 9": "Stairs Down",
    "BP FixedStairDown C 10": "Stairs Down",
    "BP FixedStairEscape C 1": "Escape Rope",
    "BP FixedStairEscape C 2": "Escape Elevator",
    "BP FixedStairEscape C 3": "Escape Elevator",
    "BP CaveExit C 0": "Escape Stairs",
    "BP FixedStairEscape C 0": "Escape Stairs",
    "BP FixedStairEscape C 4": "Escape Stairs",
    "BP FixedStairEscape C 5": "Escape Stairs",
    "BP FixedStairEscape C 6": "Escape Stairs",
    "BP FixedStairEscape C 7": "Escape Stairs",
    "BP FixedStairEscape C 8": "Escape Stairs",
    "BP FixedStairEscape C 9": "Escape Stairs",
    "BP FixedStairEscape C 10": "Escape Stairs",
    "BP FixedStairEscape C 11": "Escape Stairs",

    "BP DungeonEscape C 0": "Escape Stairs",
    "BP DungeonEscape C 1": "Escape Stairs",
    "BP DungeonEscape C 2": "Escape Stairs",
    "BP DungeonEscape C 3": "Escape Stairs",
    "BP DungeonEscape C 4": "Escape Stairs",
    "BP DungeonEscape C 5": "Escape Stairs",
    "BP DungeonEscape C 6": "Escape Stairs",
     "BP DungeonEscape C 7": "Escape Stairs",
    "BP DungeonEscape C 8": "Escape Stairs",
    "BP DungeonEscape C 9": "Escape Stairs",
    "BP DungeonEscape C 10": "Escape Stairs",
    "BP DungeonEscape C 11": "Escape Stairs",

    "BP DungeonDown C 0": "Old Stairs Down",

};


const LEGEND_CATEGORY_ORDER = [
    'Chests',
    'Shrines',
    'Monsters',
    'Extractions / Player Spawns',
    'Miscellaneous',
    'Loot Spawns',
];
const DESIRED_MAP_ORDER = [
    'Cave',
    'Ruins',
    'Crypt',
    'Inferno',
    'IceCave',
    'IceAbyss'
];
const MAP_DISPLAY_NAMES = {
    'Cave': 'Goblin Cave',
    'Ruins': 'Ruins of Forgotten Castle',
    'Crypt': 'Crypts',
    'Inferno': 'Inferno',
    'IceCave': 'Frost Mountain',
    'IceAbyss': 'Ice Abyss'
};


const getIconIdentifier = (icon) => {
    return icon?.object_name || icon?.marker?.object_name || '';
};

const getIconLocalizedString = (icon) => {
    return icon?.LocalizedString || icon?.marker?.LocalizedString || '';
};

const setIconLocalizedString = (icon, newName) => {
    const newIcon = { ...icon };
    if (newIcon.marker) {
        newIcon.marker = { ...newIcon.marker, LocalizedString: newName };
    } else {
        newIcon.LocalizedString = newName;
    }
    return newIcon;
};


const MapLegend = (props) => {
    const {
        availableMapTypes, selectedMapType, setSelectedMapType,
        focusedModuleName, focusOnModule,
        gameMode, setGameMode,
        iconStates,
        mapIconScale, setMapIconScale, moduleIconScale, setModuleIconScale,
        legendScale, setLegendScale, zoom,
        selectedCharacter, characters, setAndStoreCharacters,
        isLoadingMapData,
        itemSearchTerm, setItemSearchTerm,
        itemSearchResult, searchItems, clearItemSearch,
        isBuildingIndex, isSearchIndexReady,
        moduleDetailsForLegend,
        showModuleNamesOnMap, setShowModuleNamesOnMap,
    } = props;

    const { selectedIconNames, setSelectedIconNames } = useIconSelection();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [isLoadingThumbs, setIsLoadingThumbs] = useState(false);
    const [moduleSearchTerm, setModuleSearchTerm] = useState('');
    const [activeModuleCollapseKey, setActiveModuleCollapseKey] = useState(['modulesPanel']);

    const mapTypeHelp = (
    <div>
        <p><strong>Map Types control what content is displayed:</strong></p>
        <ul>
            <li><strong>Select Map Type:</strong> Loads modules specific to that map.</li>
            <li><strong>Focus Module:</strong> Click a module&apos;s legend image to zoom and isolate it.</li>
            <li><strong>Unfocus Module:</strong> Click its legend image again, or use <strong>Reset Map View</strong> (bottom-right).</li>
            <li><strong>Search Modules:</strong> Find modules by their in-game names.</li>
            <li><strong>Toggle Names:</strong> Use <strong>Show Names</strong> to display/hide module names on the map.</li>
        </ul>
    </div>
    );

    const modeHelp = (
        <div>
            <p>
                Select a dungeon difficulty to view its specific spawns and drop tables:
            </p>
            <ul>
                <li><strong>Adventure Mode:</strong> Up to Epic Quality</li>
                <li><strong>Normal Mode:</strong> Up to Legendary Quality</li>
                <li><strong>HR Mode:</strong> Up to Artifact Quality (e.g., named weapons)</li>
            </ul>
        </div>
    );

    const itemSearchHelp = (
        <div>
            <p><strong>Find specific items and their drop rates:</strong></p>
            <ul>
                <li><strong>Search for an Item:</strong> Filters markers (spawns) and modules to show only those containing the item.</li>
                <li><strong>View Drop Rate:</strong>
                    <ol style={{ paddingLeft: '20px', margin: '5px 0' }}>
                        <li>Select a filtered marker.</li>
                        <li>In the marker&apos;s popup, use its <em>top search bar</em> to find the item and see its drop rate.</li>
                    </ol>
                </li>
            </ul>
        </div>
    );

    const markerHelp = (
        <div>
            <p><strong>Interacting with Map Markers (Spawns):</strong></p>
            <ul>
                <li><strong>Toggle Visibility:</strong> Select/deselect markers in the legend to show/hide them on the map.</li>
                <li><strong>View Loot Table:</strong> Click a marker on the map to see its potential item drops (if available).</li>
                <li><strong>Filter Loot:</strong> Use the search bar <em>within the marker&apos;s popup</em> to find specific items in its loot table.</li>
                <li><strong>Important:</strong> Item availability varies by Map Type and Mode. If an item seems missing, try checking other modes/maps.</li>
            </ul>
        </div>
    );

    const commonDisabled = isLoadingMapData || !selectedMapType;

    useEffect(() => {
        if (!selectedMapType || (moduleDetailsForLegend && moduleDetailsForLegend.length === 0)) {
            setModuleSearchTerm('');
        }
    }, [selectedMapType, moduleDetailsForLegend]);

    const handleItemSearchChange = (e) => {
        setItemSearchTerm(e.target.value);
        if (e.target.value === '') {
            clearItemSearch();
        }
    };
    const handleItemSearch = (value) => {
        searchItems(value);
    };

    const filteredIconStates = useMemo(() => {
        if (!iconStates || Object.keys(iconStates).length === 0) return {};

        const itemSearchActiveAndFound = itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources && itemSearchResult.foundSources.size > 0;

        const applyRemappingToIcon = (iconInstance) => {
            const originalObjectName = getIconIdentifier(iconInstance);
            const remappedName = NAME_REMAPPING[originalObjectName];

            if (remappedName) {
                return setIconLocalizedString(iconInstance, remappedName);
            }
            return iconInstance;
        };

        if (!searchTerm && !itemSearchActiveAndFound) {
            const remappedStates = {};
            Object.entries(iconStates).forEach(([category, iconsOrSubcategories]) => {
                if (Array.isArray(iconsOrSubcategories)) {
                    remappedStates[category] = iconsOrSubcategories.map(applyRemappingToIcon);
                } else if (typeof iconsOrSubcategories === 'object' && iconsOrSubcategories !== null) {
                    const remappedSubcategories = {};
                    Object.entries(iconsOrSubcategories).forEach(([subCategory, icons]) => {
                        if (Array.isArray(icons)) {
                            remappedSubcategories[subCategory] = icons.map(applyRemappingToIcon);
                        }
                    });
                    if (Object.keys(remappedSubcategories).length > 0) {
                        remappedStates[category] = remappedSubcategories;
                    } else if (Object.keys(iconsOrSubcategories).length > 0) {
                         remappedStates[category] = remappedSubcategories;
                    }
                }
            });
            return remappedStates;
        }

        const lowerSearch = searchTerm.toLowerCase().trim();
        const globallyFilteredMarkerNames = itemSearchActiveAndFound
            ? itemSearchResult.foundSources
            : null;
        const filtered = {};

        Object.entries(iconStates).forEach(([category, iconsOrSubcategories]) => {
            const filterAndRemapLogic = icon => {
                const originalObjectName = getIconIdentifier(icon);
                const initialLocalizedStr = getIconLocalizedString(icon);
                const remappedDisplayNameForSearch = NAME_REMAPPING[originalObjectName];

                const searchObjName = originalObjectName.toLowerCase();
                const searchInitialLocalized = initialLocalizedStr.toLowerCase();
                const searchRemappedName = (remappedDisplayNameForSearch || '').toLowerCase();

                if (globallyFilteredMarkerNames && !globallyFilteredMarkerNames.has(originalObjectName)) {
                    return null;
                }

                let matchesSearchTerm = true;
                if (lowerSearch) {
                    matchesSearchTerm = searchObjName.includes(lowerSearch) ||
                                     searchInitialLocalized.includes(lowerSearch) ||
                                     (remappedDisplayNameForSearch && searchRemappedName.includes(lowerSearch));
                }

                if (!matchesSearchTerm) {
                    return null;
                }
                return applyRemappingToIcon(icon);
            };

            if (Array.isArray(iconsOrSubcategories)) {
                const processedIcons = iconsOrSubcategories.map(filterAndRemapLogic).filter(Boolean);
                if (processedIcons.length > 0) filtered[category] = processedIcons;
            } else if (typeof iconsOrSubcategories === 'object' && iconsOrSubcategories !== null) {
                const matchingSubcategories = {};
                Object.entries(iconsOrSubcategories).forEach(([subCategory, icons]) => {
                    if (Array.isArray(icons)) {
                        const processedIcons = icons.map(filterAndRemapLogic).filter(Boolean);
                        if (processedIcons.length > 0) matchingSubcategories[subCategory] = processedIcons;
                    }
                });
                if (Object.keys(matchingSubcategories).length > 0) filtered[category] = matchingSubcategories;
            }
        });
        return filtered;
    }, [iconStates, searchTerm, itemSearchTerm, itemSearchResult]);

    useEffect(() => {
        const itemSearchFilterIsActive = itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources && itemSearchResult.foundSources.size > 0;
        if (searchTerm || itemSearchFilterIsActive) {
            setActiveCollapseKeys(Object.keys(filteredIconStates));
        }
    }, [searchTerm, itemSearchTerm, itemSearchResult, filteredIconStates]);

    const toggleAllVisibleIcons = useCallback((select = true) => {
        const visibleIconNames = new Set();
        const processIconsList = (iconsList) => iconsList.forEach(icon => {
            const name = getIconIdentifier(icon);
            if (name) visibleIconNames.add(name);
        });
        Object.values(filteredIconStates).forEach(categoryData => {
            if (Array.isArray(categoryData)) processIconsList(categoryData);
            else if (typeof categoryData === 'object' && categoryData !== null) {
                Object.values(categoryData).forEach(subIcons => {
                    if (Array.isArray(subIcons)) processIconsList(subIcons);
                });
            }
        });
        setSelectedIconNames(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (select) {
                visibleIconNames.forEach(name => newSelected.add(name));
            } else {
                visibleIconNames.forEach(name => newSelected.delete(name));
            }
            return newSelected;
        });
    }, [filteredIconStates, setSelectedIconNames]);

    const toggleSection = useCallback((category, subCategory = null) => {
        if (!iconStates) return;
        const targetIconsSource = [];
        let sourceData = subCategory ? iconStates[category]?.[subCategory] : iconStates[category];
        if (!sourceData) return;

        if (Array.isArray(sourceData)) {
            targetIconsSource.push(...sourceData);
        } else if (typeof sourceData === 'object' && sourceData !== null && !subCategory) {
            Object.values(sourceData).forEach(subIcons => {
                if (Array.isArray(subIcons)) targetIconsSource.push(...subIcons);
            });
        }

        const uniqueIconNamesInSection = new Set(targetIconsSource.map(icon => getIconIdentifier(icon)).filter(Boolean));
        if (uniqueIconNamesInSection.size === 0) return;

        let relevantNamesForToggle = uniqueIconNamesInSection;
        const itemSearchActiveAndFound = itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources && itemSearchResult.foundSources.size > 0;
        if (itemSearchActiveAndFound) {
            relevantNamesForToggle = new Set();
            uniqueIconNamesInSection.forEach(nameKey => {
                if (itemSearchResult.foundSources.has(nameKey)) {
                    relevantNamesForToggle.add(nameKey);
                }
            });
        }

        if (relevantNamesForToggle.size === 0) return;

        const isAnyCurrentlySelected = Array.from(relevantNamesForToggle).some(nameKey => selectedIconNames.has(nameKey));
        const shouldSelect = !isAnyCurrentlySelected;

        setSelectedIconNames(prevSelected => {
            const newSet = new Set(prevSelected);
            relevantNamesForToggle.forEach(key => {
                if (shouldSelect) newSet.add(key);
                else newSet.delete(key);
            });
            return newSet;
        });
    }, [iconStates, selectedIconNames, setSelectedIconNames, itemSearchTerm, itemSearchResult.searchPerformed, itemSearchResult.foundSources]);

    const renderLegendSvg = useCallback((icon) => {
        const objectName = getIconIdentifier(icon);
        const iconId = getImageId(objectName);
        const svg = getMapIcons()[iconId] || getMapIcons()['misc'];
        return scaleSvgIcon(svg, legendScale);
    }, [legendScale]);

    const renderIconsList = useCallback((iconsArray) => {
        if (!iconsArray || iconsArray.length === 0) return [];

        const uniqueIconsByFinalDisplayName = new Map();

        iconsArray.forEach(icon => {
            const originalNameKey = getIconIdentifier(icon);
            if (!originalNameKey) return;

            const localizedNameFromData = getIconLocalizedString(icon);
            let finalDisplayNameForKey = cleanMonsterId(originalNameKey);

            if (localizedNameFromData && localizedNameFromData !== "N/A" && localizedNameFromData.trim() !== "") {
                finalDisplayNameForKey = localizedNameFromData;
            }

            if(NAME_REMAPPING[finalDisplayNameForKey]){
                finalDisplayNameForKey = NAME_REMAPPING[finalDisplayNameForKey]
            }

            if (!uniqueIconsByFinalDisplayName.has(finalDisplayNameForKey)) {
                uniqueIconsByFinalDisplayName.set(finalDisplayNameForKey, {
                    originalKeys: new Set([originalNameKey]),
                    dataObjectForIcon: icon
                });
            } else {
                uniqueIconsByFinalDisplayName.get(finalDisplayNameForKey).originalKeys.add(originalNameKey);
            }
        });

        return Array.from(uniqueIconsByFinalDisplayName.entries())
            .sort(([displayNameA], [displayNameB]) => displayNameA.localeCompare(displayNameB))
            .map(([displayLabel, { originalKeys, dataObjectForIcon }]) => {
                const isAnyAssociatedKeySelected = Array.from(originalKeys).some(key => selectedIconNames.has(key));
                const isVisible = isAnyAssociatedKeySelected;

                const handleClick = (e) => {
                    e.stopPropagation();
                    const shouldSelectAll = !isAnyAssociatedKeySelected;
                    setSelectedIconNames(prevSelected => {
                        const newSet = new Set(prevSelected);
                        originalKeys.forEach(key => {
                            if (shouldSelectAll) newSet.add(key);
                            else newSet.delete(key);
                        });
                        return newSet;
                    });
                };

                if (searchTerm) {
                    const lowerSearchLegend = searchTerm.toLowerCase().trim();
                    const displayLabelLower = displayLabel.toLowerCase();
                    let matchesLegendSearch = displayLabelLower.includes(lowerSearchLegend);
                    if (!matchesLegendSearch) {
                        matchesLegendSearch = Array.from(originalKeys).some(key =>
                            key.toLowerCase().includes(lowerSearchLegend) || cleanMonsterId(key).toLowerCase().includes(lowerSearchLegend)
                        );
                    }
                    if (!matchesLegendSearch) return null;
                }

                const originalKeysString = Array.from(originalKeys).join(', ');
                const title = `${displayLabel} (IDs: ${originalKeysString}) (${isVisible ? 'Visible' : 'Hidden'})`;

                return (
                    <Col xs={12} sm={12} md={12} lg={12} xl={12} key={displayLabel + originalKeysString}>
                        <div
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px', opacity: isVisible ? 1 : 0.6, transition: 'opacity 0.2s ease-in-out', padding: '2px 0' }}
                            onClick={handleClick} role="button" tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}
                            title={title}
                        >
                            <div
                                dangerouslySetInnerHTML={{ __html: renderLegendSvg(dataObjectForIcon) }}
                                style={{ flexShrink: 0, marginRight: 8, width: legendScale, height: legendScale }}
                                aria-hidden="true"
                            />
                            <Text ellipsis style={{ color: isVisible ? '#eee' : '#bbb', userSelect: 'none' }}>
                                {displayLabel}
                            </Text>
                        </div>
                    </Col>
                );
            }).filter(Boolean);
    }, [selectedIconNames, setSelectedIconNames, searchTerm, renderLegendSvg, legendScale]);

    const checkSelectionStatus = useCallback((icons) => {
        if (!icons || icons.length === 0) return { someSelected: false, allSelected: false };
        let iconNameKeys = new Set(icons.map(i => getIconIdentifier(i)).filter(Boolean));
        if (iconNameKeys.size === 0) return { someSelected: false, allSelected: false };

        const itemSearchActiveAndFound = itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources && itemSearchResult.foundSources.size > 0;
        if (itemSearchActiveAndFound) {
            const relevantNames = new Set();
            iconNameKeys.forEach(name => {
                if (itemSearchResult.foundSources.has(name)) relevantNames.add(name);
            });
            iconNameKeys = relevantNames;
            if (iconNameKeys.size === 0) return { someSelected: false, allSelected: false };
        }
        let visibleCount = Array.from(iconNameKeys).filter(nameKey => selectedIconNames.has(nameKey)).length;
        return { someSelected: visibleCount > 0, allSelected: visibleCount === iconNameKeys.size };
    }, [selectedIconNames, itemSearchTerm, itemSearchResult.searchPerformed, itemSearchResult.foundSources]);

    const collapseItems = useMemo(() => {
        if (!filteredIconStates || Object.keys(filteredIconStates).length === 0) return [];

        const allCategoryNamesInFiltered = Object.keys(filteredIconStates);
        const orderedCategories = LEGEND_CATEGORY_ORDER.filter(cat => allCategoryNamesInFiltered.includes(cat));
        const unorderedCategories = allCategoryNamesInFiltered
            .filter(cat => !LEGEND_CATEGORY_ORDER.includes(cat))
            .sort((a, b) => a.localeCompare(b));
        const finalCategoryOrder = [...orderedCategories, ...unorderedCategories];

        return finalCategoryOrder.map(category => {
            const iconsOrSubcategories = filteredIconStates[category];
            let panelContent;

            const originalCategoryEntry = iconStates ? iconStates[category] : null;
            let allIconsInOriginalCategoryForCount = [];
            if (originalCategoryEntry) {
                 if (Array.isArray(originalCategoryEntry)) {
                    allIconsInOriginalCategoryForCount.push(...originalCategoryEntry);
                } else if (typeof originalCategoryEntry === 'object' && originalCategoryEntry !== null) {
                    Object.values(originalCategoryEntry).forEach(subArray => {
                        if (Array.isArray(subArray)) allIconsInOriginalCategoryForCount.push(...subArray);
                    });
                }
            }

            let originalCategoryIconNames = new Set(allIconsInOriginalCategoryForCount.map(i => getIconIdentifier(i)).filter(Boolean));
            const itemSearchActiveAndFound = itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources && itemSearchResult.foundSources.size > 0;
            if (itemSearchActiveAndFound) {
                const relevantNames = new Set();
                originalCategoryIconNames.forEach(name => {
                    if (itemSearchResult.foundSources.has(name)) relevantNames.add(name);
                });
                originalCategoryIconNames = relevantNames;
            }
            const totalUniqueCountOriginal = originalCategoryIconNames.size;

            if (totalUniqueCountOriginal === 0 && itemSearchActiveAndFound && !searchTerm) return null;


            const categoryHasSubcategories = typeof iconsOrSubcategories === 'object' && !Array.isArray(iconsOrSubcategories) && iconsOrSubcategories !== null;

            if (categoryHasSubcategories) {
                const subCategorySections = Object.entries(iconsOrSubcategories)
                    .sort(([subA], [subB]) => subA.localeCompare(subB))
                    .map(([subCategory, icons]) => {
                        if (!Array.isArray(icons) || icons.length === 0) return null;

                        let originalSubCategoryIcons = iconStates?.[category]?.[subCategory] || [];
                        let uniqueIconsInOriginalSub = new Set(originalSubCategoryIcons.map(i => getIconIdentifier(i)).filter(Boolean));
                        if (itemSearchActiveAndFound) {
                            const relevantNamesSub = new Set();
                            uniqueIconsInOriginalSub.forEach(name => {
                                if (itemSearchResult.foundSources.has(name)) relevantNamesSub.add(name);
                            });
                            uniqueIconsInOriginalSub = relevantNamesSub;
                        }
                        const uniqueCountSub = uniqueIconsInOriginalSub.size;
                        if (uniqueCountSub === 0 && itemSearchActiveAndFound && !searchTerm) return null;

                        const renderedList = renderIconsList(icons);
                        if (renderedList.length === 0) return null;

                        let currentSelectedCountSub = Array.from(uniqueIconsInOriginalSub).filter(name => selectedIconNames.has(name)).length;

                        return (
                            <div key={subCategory} style={{ marginBottom: '15px' }}>
                                <Divider orientation="left" plain style={{ marginTop: 0, marginBottom: '10px', borderColor: '#555' }}>
                                    <Button type="text" size="small" onClick={(e) => { e.stopPropagation(); toggleSection(category, subCategory); }} style={{ color: '#eee', padding: '0 5px' }} title={`Toggle all in ${subCategory}`}>
                                        {subCategory} ({currentSelectedCountSub}/{uniqueCountSub})
                                    </Button>
                                </Divider>
                                <Row gutter={[8, 8]}>{renderedList}</Row>
                            </div>
                        );
                    }).filter(Boolean);
                panelContent = <div>{subCategorySections}</div>;
                if (subCategorySections.length === 0 && !searchTerm && !itemSearchActiveAndFound) return null;

            } else if (Array.isArray(iconsOrSubcategories)) {
                if (iconsOrSubcategories.length === 0) return null;
                const renderedList = renderIconsList(iconsOrSubcategories);
                panelContent = <Row gutter={[8, 8]}>{renderedList}</Row>;
                if (renderedList.length === 0 && !searchTerm && !itemSearchActiveAndFound) return null;
            } else {
                return null;
            }

            if (!panelContent || (panelContent.props.children && Array.isArray(panelContent.props.children) && panelContent.props.children.every(child => !child))) {
                 if (!searchTerm && !itemSearchActiveAndFound) return null;
            }

            const { allSelected: isAllInCategorySelected } = checkSelectionStatus(allIconsInOriginalCategoryForCount);
            const panelLabel = `${category} (${totalUniqueCountOriginal})`;
            const extraButton = (
                <Button size="small" onClick={(e) => { e.stopPropagation(); toggleSection(category); }} title={`${isAllInCategorySelected ? 'Deselect' : 'Select'} all in ${category}`}>
                    {isAllInCategorySelected ? 'Deselect All' : 'Select All'}
                </Button>
            );

            return {
                key: category, label: panelLabel, children: panelContent, extra: extraButton,
                style: { background: 'transparent', borderBottom: '1px solid #444' }
            };
        }).filter(Boolean);
    }, [filteredIconStates, iconStates, searchTerm, itemSearchTerm, itemSearchResult, renderIconsList, checkSelectionStatus, toggleSection, selectedIconNames, LEGEND_CATEGORY_ORDER]);

    const mapTypeOptions = useMemo(() => Object.keys(availableMapTypes || {}).sort(), [availableMapTypes]);

    const modulesForSelectedType = useMemo(() => {
        if (!selectedMapType || !moduleDetailsForLegend || moduleDetailsForLegend.length === 0) {
            setIsLoadingThumbs(false);
            return [];
        }
        setIsLoadingThumbs(true);
        const modules = moduleDetailsForLegend.map(detail => {
            const displayName = (detail.Module_LocalizedString && detail.Module_LocalizedString !== "N/A")
                ? detail.Module_LocalizedString
                : cleanMonsterId(detail.moduleName);
            return {
                moduleName: detail.moduleName,
                displayName: displayName,
                imagePath: detail.imagePath,
            };
        });
        Promise.resolve().then(() => setIsLoadingThumbs(false));
        return modules;
    }, [selectedMapType, moduleDetailsForLegend]);


    const filteredModules = useMemo(() => {
        if (!modulesForSelectedType.length) return [];
        if (!moduleSearchTerm.trim()) return modulesForSelectedType;
        const lowerSearchTerm = moduleSearchTerm.toLowerCase();
        return modulesForSelectedType.filter(module =>
            module.displayName.toLowerCase().includes(lowerSearchTerm) ||
            module.moduleName.toLowerCase().includes(lowerSearchTerm)
        );
    }, [modulesForSelectedType, moduleSearchTerm]);

    const handleMapTypeChange = useCallback((value) => { setSelectedMapType(value); }, [setSelectedMapType]);
    const handleModuleSelect = useCallback((moduleEntry) => {
        if (moduleEntry && moduleEntry.moduleName) focusOnModule(moduleEntry.moduleName);
    }, [focusOnModule]);

    const handleGameModeChange = useCallback((newModeKey) => {
        setGameMode(newModeKey);
    }, [setGameMode]);

    const modes = Object.values(GAME_MODES);
    const orderedMapOptions = DESIRED_MAP_ORDER
        .filter(mapKey => mapTypeOptions.includes(mapKey))
        .map(mapKey => (
            <Option key={mapKey} value={mapKey}>
                {MAP_DISPLAY_NAMES[mapKey] || mapKey}
            </Option>
        ));

    const tabItems = useMemo(() => [
        {
            key: '1', label: 'Selection & Markers', children: (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '0 8px', flexShrink: 0 }}>
                        <Divider orientation="left" plain style={{ marginTop: '8px', marginBottom: '0px', borderColor: '#555', color: '#aaa' }}>
                            <div> Map Type <HelpPopover content={mapTypeHelp} title="Map Selection Details" /> </div>
                        </Divider>
                        <Select
                            value={selectedMapType} style={{ width: '100%', marginBottom: '15px' }}
                            placeholder={mapTypeOptions.length > 0 ? "Select Map Type..." : "Loading Maps..."}
                            onChange={handleMapTypeChange} disabled={mapTypeOptions.length === 0 || isLoadingMapData}
                            showSearch optionFilterProp="children"
                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Map Types Found" />}
                        >
                            {orderedMapOptions.length > 0 ? orderedMapOptions : (mapTypeOptions.length > 0 && <Option disabled key="no_desired_maps">No matching maps found</Option> )}
                        </Select>
                    </div>
                    <div style={{ padding: '0 8px', flexShrink: 0, marginBottom: '15px' }}>
                        <Collapse activeKey={activeModuleCollapseKey} onChange={setActiveModuleCollapseKey} ghost>
                            <Collapse.Panel
                                header={`Module (${moduleSearchTerm.trim() && modulesForSelectedType.length > 0 ? `${filteredModules.length} / ` : ''}${modulesForSelectedType.length})`}
                                extra={ <span title={`Click to ${showModuleNamesOnMap ? 'hide' : 'show'} module names on map`}> <Text style={{ marginRight: 8, color: '#ccc', fontSize: '0.85em' }}>Show Names</Text> <Switch size="small" checked={showModuleNamesOnMap} onChange={(checked, event) => { event.stopPropagation(); setShowModuleNamesOnMap(checked); }} /> </span> }
                                key="modulesPanel" style={{ background: 'transparent', borderBottom: '1px solid #444' }}
                            >
                                <Input placeholder="Search modules..." value={moduleSearchTerm} onChange={(e) => setModuleSearchTerm(e.target.value)} allowClear prefix={<SearchOutlined />} style={{ marginBottom: '10px' }} disabled={isLoadingMapData || !selectedMapType || modulesForSelectedType.length === 0} />
                                <div style={{ overflowY: 'auto', minHeight: '100px', maxHeight: '220px', padding: '8px', border: '1px solid #444', borderRadius: '4px', background: '#2a3038' }}>
                                    {isLoadingMapData ? <div style={{ textAlign: 'center', padding: '20px' }}><Spin tip="Loading map data..." /></div>
                                    : !selectedMapType ? <div style={{ textAlign: 'center', color: '#aaa', padding: '20px' }}>Select a map type.</div>
                                    : isLoadingThumbs ? <div style={{ textAlign: 'center', padding: '20px' }}><Spin tip="Loading thumbnails..." /></div>
                                    : modulesForSelectedType.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No modules for map." />
                                    : filteredModules.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No modules match search." />
                                    : <Row gutter={[12, 12]}> {filteredModules.map((entry) => ( <Col key={entry.moduleName} xs={8} sm={8} md={8} lg={8} xl={8}> <ModuleThumbnail entry={{ ...entry, title: entry.displayName }} isSelected={focusedModuleName === entry.moduleName} onClick={() => handleModuleSelect(entry)} /> </Col> ))} </Row>
                                    }
                                </div>
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                    <div style={{ padding: '0 8px 10px 8px', flexShrink: 0 }}>
                        <Divider orientation="left" plain style={{ borderColor: '#555', color: '#aaa', marginTop: '0px', marginBottom: '10px' }}> <div> Mode <HelpPopover content={modeHelp} title="Dungeon Mode Details" /> </div> </Divider>
                        <div> <Row gutter={[10, 0]}> <Col span={24}> <div style={{ display: 'flex', justifyContent: 'center' }}> <Button.Group style={{ width: '100%', display: 'flex' }}> {modes.map(mode => ( <Button key={mode.key} type={mode.key === gameMode ? "primary" : "default"} onClick={() => handleGameModeChange(mode.key)} style={{ flex: 1, textAlign: 'center' }} disabled={commonDisabled} > {mode.label} </Button> ))} </Button.Group> </div> </Col> </Row> </div>
                    </div>
                    <div style={{ padding: '0 8px 10px 8px', flexShrink: 0 }}>
                        <Divider orientation="left" plain style={{ borderColor: '#555', color: '#aaa', marginTop: '0px', marginBottom: '10px' }}> <div> Item Search <HelpPopover content={itemSearchHelp} title="Item Search Details"/> </div> </Divider>
                        <AntSearch placeholder={isBuildingIndex ? "Building index..." : (!isSearchIndexReady ? "Initializing..." : "Search item name...")} enterButton="Search" value={itemSearchTerm} onChange={handleItemSearchChange} onSearch={handleItemSearch} loading={isBuildingIndex} allowClear disabled={isLoadingMapData || isBuildingIndex || !isSearchIndexReady || !selectedMapType} />
                        {itemSearchResult.searchPerformed && ( <div style={{ marginTop: '8px', maxHeight: '150px', overflowY: 'auto', fontSize: '0.8em', paddingLeft: '5px' }}>
                            {itemSearchResult.error && (!itemSearchResult.foundItemNames || itemSearchResult.foundItemNames.length === 0) && <Text type="danger">{itemSearchResult.error}</Text> }
                            {itemSearchResult.foundItemNames && itemSearchResult.foundItemNames.length > 0 && ( <div style={{ marginBottom: '5px' }}> <Text style={{ color: '#eee' }}> Found ({itemSearchResult.foundItemNames.length > 10 ? `Top 10/${itemSearchResult.foundItemNames.length}` : itemSearchResult.foundItemNames.length}): </Text> <List size="small" dataSource={itemSearchResult.foundItemNames.slice(0, 10)} renderItem={(itemName) => ( <List.Item style={{ padding: '2px 0' }}><Text style={{ color: '#ccc' }}>{itemName}</Text></List.Item> )} locale={{ emptyText: ' ' }} /> </div> )}
                            {itemSearchResult.foundSources.size > 0 && !itemSearchResult.error && <Text style={{ color: '#aaa', display: 'block', marginTop: '4px' }}> Legend filtered. {itemSearchResult.filteredModuleNames && `Map filtered to ${itemSearchResult.filteredModuleNames.size} module(s).`} </Text> }
                        </div>)}
                    </div>
                    <div style={{ flexGrow: 1, overflowY: 'auto', padding: '0 8px', display: 'flex', flexDirection: 'column' }}>
                        <Divider orientation="left" plain style={{ borderColor: '#555', color: '#aaa', marginTop: '10px', flexShrink: 0 }}> <div> Marker Visibility <HelpPopover content={markerHelp} title="Marker Selection Details"/> </div> </Divider>
                        <Row gutter={8} style={{ marginBottom: 10, flexShrink: 0 }}> <Col span={12}><Button block onClick={() => toggleAllVisibleIcons(true)} size="small" disabled={isLoadingMapData || collapseItems.length === 0}>Select Visible</Button></Col> <Col span={12}><Button block onClick={() => toggleAllVisibleIcons(false)} size="small" disabled={isLoadingMapData || collapseItems.length === 0}>Deselect Visible</Button></Col> </Row>
                        <Input placeholder="Filter markers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear style={{ marginBottom: 10, flexShrink: 0 }} prefix={<SearchOutlined />} disabled={isLoadingMapData || Object.keys(iconStates).length === 0} />
                        {isLoadingMapData && collapseItems.length === 0 && Object.keys(iconStates).length === 0 ? <div style={{ textAlign: 'center', padding: '20px' }}><Spin tip="Loading markers..." /></div>
                        : collapseItems.length > 0 ? <Collapse accordion={!searchTerm && !(itemSearchTerm && itemSearchResult.searchPerformed && itemSearchResult.foundSources.size > 0)} activeKey={activeCollapseKeys} onChange={setActiveCollapseKeys} ghost items={collapseItems} style={{ overflowY: 'auto', flexGrow: 1 }} />
                        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={ (searchTerm || (itemSearchTerm && itemSearchResult.searchPerformed)) ? "No matching markers" : (isLoadingMapData ? "Loading..." : "No markers for map/mode") } style={{ flexShrink: 0, marginTop: '20px' }} />
                        } <div style={{ height: '10px', flexShrink: 0 }}></div>
                    </div>
                </div>
            )
        },
        { key: '2', label: 'Active Quests', children: (
            <div style={{ height: '100%', overflowY: 'auto', padding: '0 8px' }}>
                <Divider orientation="left" plain style={{ borderColor: '#555', color: '#aaa', marginTop: '0px' }}>Active Quests</Divider>
                <MapActiveQuests mapName={selectedMapType || ''} characters={characters || []} setAndStoreCharacters={setAndStoreCharacters} selectedCharacter={selectedCharacter} />
                <div style={{ height: '10px' }}></div>
            </div>
        )},
        { key: '3', label: 'Settings', children: (
            <div style={{ height: '100%', overflowY: 'auto', padding: '0 8px' }}>
                <MapSettings mapIconScale={mapIconScale} setMapIconScale={setMapIconScale} legendScale={legendScale} setLegendScale={setLegendScale} moduleIconScale={moduleIconScale} setModuleIconScale={setModuleIconScale} />
                <Divider orientation="left" plain style={{ borderColor: '#555', color: '#aaa' }}>Map Controls</Divider>
                <div style={{ padding: '0 8px' }}> <Text style={{ color: '#EEEEEE', display: 'block', marginBottom: '4px' }}> Map Zoom: {zoom.toFixed(2)} </Text> </div>
                <div style={{ height: '10px' }}></div>
            </div>
        )},
    ], [
        availableMapTypes, selectedMapType, gameMode, focusedModuleName, moduleDetailsForLegend,
        isLoadingThumbs, isLoadingMapData,
        handleMapTypeChange, mapTypeOptions, orderedMapOptions, handleModuleSelect, handleGameModeChange,
        iconStates, collapseItems,
        searchTerm, activeCollapseKeys, toggleAllVisibleIcons,
        selectedCharacter, characters, setAndStoreCharacters,
        mapIconScale, moduleIconScale, legendScale, zoom,
        itemSearchTerm, itemSearchResult, isBuildingIndex, isSearchIndexReady, handleItemSearchChange, handleItemSearch,
        moduleSearchTerm, activeModuleCollapseKey, filteredModules,
        showModuleNamesOnMap, setShowModuleNamesOnMap,
        commonDisabled, modes,
        mapTypeHelp, modeHelp, itemSearchHelp, markerHelp
    ]);

    return (
        <div style={{ height: '100%', background: '#31363F', display: 'flex', flexDirection: 'column' }}>
            <Tabs
                activeKey={activeTabKey} onChange={setActiveTabKey} type="card" size="small" items={tabItems}
                style={{ color: '#EEEEEE', flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                tabBarStyle={{ flexShrink: 0, marginBottom: '0px', padding: '0 5px', background: '#2a3038' }}
                contentStyle={{ flexGrow: 1, overflow: 'hidden', height: 'calc(100% - 32px)', padding: '10px 0 0 0' }}
            />
        </div>
    );
}

MapLegend.propTypes = {
    availableMapTypes: PropTypes.object.isRequired,
    selectedMapType: PropTypes.string,
    setSelectedMapType: PropTypes.func.isRequired,
    focusedModuleName: PropTypes.string,
    focusOnModule: PropTypes.func.isRequired,
    gameMode: PropTypes.string.isRequired,
    setGameMode: PropTypes.func.isRequired,
    iconStates: PropTypes.object.isRequired,
    mapIconScale: PropTypes.number.isRequired, setMapIconScale: PropTypes.func.isRequired,
    moduleIconScale: PropTypes.number.isRequired, setModuleIconScale: PropTypes.func.isRequired,
    legendScale: PropTypes.number.isRequired, setLegendScale: PropTypes.func.isRequired,
    zoom: PropTypes.number.isRequired, setZoom: PropTypes.func.isRequired,
    selectedCharacter: PropTypes.string, characters: PropTypes.array, setAndStoreCharacters: PropTypes.func,
    isLoadingMapData: PropTypes.bool.isRequired,
    itemSearchTerm: PropTypes.string.isRequired, setItemSearchTerm: PropTypes.func.isRequired,
    itemSearchResult: PropTypes.shape({
        foundItemNames: PropTypes.arrayOf(PropTypes.string),
        foundSources: PropTypes.instanceOf(Set).isRequired,
        filteredModuleNames: PropTypes.instanceOf(Set),
        searchPerformed: PropTypes.bool.isRequired,
        error: PropTypes.string,
    }).isRequired,
    searchItems: PropTypes.func.isRequired, clearItemSearch: PropTypes.func.isRequired,
    isBuildingIndex: PropTypes.bool.isRequired,
    isSearchIndexReady: PropTypes.bool.isRequired,
    moduleDetailsForLegend: PropTypes.arrayOf(PropTypes.shape({
        moduleName: PropTypes.string.isRequired,
        imagePath: PropTypes.string.isRequired,
        Module_LocalizedString: PropTypes.string,
    })),
    showModuleNamesOnMap: PropTypes.bool.isRequired,
    setShowModuleNamesOnMap: PropTypes.func.isRequired,
};
MapLegend.defaultProps = {
    moduleDetailsForLegend: [],
    selectedCharacter: null,
    characters: [],
    setAndStoreCharacters: () => { },
    focusedModuleName: null,
    selectedMapType: null,
};

export default MapLegend;